import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import $ from "jquery"; // Assuming jQuery is available

const TripAdvisor = (props) => {
  const {
    location: locationId,
    apikey,
    talogo, // Raw HTML string from Rails helper
    tareviewtext,
    readfullreviewtext,
    outboundtext,
    writereviewtext,
  } = props;

  // State for data fetched from API
  const [taData, setTaData] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [overallRatingImage, setOverallRatingImage] = useState(null);
  const [webUrl, setWebUrl] = useState(null);
  const [numReviews, setNumReviews] = useState(null);
  const [writeUrl, setWriteUrl] = useState(null);

  // State for currently displayed review
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [currentReview, setCurrentReview] = useState(null);

  const intervalRef = useRef(null);

  // Fetch data on mount
  useEffect(() => {
    const getTaData = () => {
      // Construct the API URL carefully
      // Note: Consider CORS issues. This might need to be proxied via your Rails backend
      // if direct client-side calls to api.tripadvisor.com are blocked.
      const apiUrl = `https://api.tripadvisor.com/api/partner/2.0/location/${locationId}?key=${apikey}`;
      $.get(apiUrl, null, null, "json") // Removed the explicit callback here
        .done((data) => {
          // Process data on success
          if (data && data.reviews && data.reviews.length > 0) {
            setTaData(data);
            setReviews(data.reviews);
            setOverallRatingImage(data.rating_image_url);
            setWebUrl(data.web_url);
            setNumReviews(data.num_reviews);
            setWriteUrl(data.write_review_url); // Assuming API returns write_review_url
            setCurrentReview(data.reviews[0]); // Set initial review
            setCurrentReviewIndex(0);
          } else {
            console.warn(
              "TripAdvisor API returned no reviews or invalid data for location:",
              locationId,
              data
            );
            // Handle state when no reviews are found
            setOverallRatingImage(data?.rating_image_url); // Still show overall rating if available
            setWebUrl(data?.web_url);
            setNumReviews(data?.num_reviews || "0"); // Show 0 reviews
            setWriteUrl(data?.write_review_url);
          }
        })
        .fail((xhr, status, error) => {
          console.error(
            `Failed to fetch TripAdvisor data for location ${locationId}: ${status}, ${error}`
          );
          // Handle error state if needed
        });
    };

    if (locationId && apikey) {
      getTaData();
    } else {
      console.warn("Missing locationId or apikey for TripAdvisor component.");
    }

    // Cleanup function for interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [locationId, apikey]); // Re-fetch if locationId or apikey changes

  // Effect to cycle through reviews
  useEffect(() => {
    const iterateReviews = () => {
      if (reviews.length > 1) {
        // Only cycle if there's more than one review
        setCurrentReviewIndex((prevIndex) => {
          const nextIndex = (prevIndex + 1) % reviews.length; // Loop back to 0
          setCurrentReview(reviews[nextIndex]);
          return nextIndex;
        });
      }
    };

    // Start timer only after reviews are loaded and if there are multiple reviews
    if (reviews && reviews.length > 1) {
      // Clear previous interval if reviews array changes
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(iterateReviews, 6000);
    }

    // Cleanup interval when reviews change or component unmounts (handled by main useEffect cleanup)
    // Return cleanup specific to *this* effect if needed
    // return () => { if (intervalRef.current) clearInterval(intervalRef.current); }
  }, [reviews]); // Dependency on the reviews array

  // Don't render if essential data hasn't loaded yet
  if (!taData && !numReviews) {
    // Allow rendering if numReviews is 0 but other data exists
    return <div className="ta-cell">Loading TripAdvisor data...</div>; // Or some loading indicator
  }

  return (
    <div className="ta-cell">
      <div className="ta-member">
        {/* Use dangerouslySetInnerHTML carefully. Ensure props.talogo is trusted. */}
        <a
          target="_blank"
          rel="noopener noreferrer" // Add rel for security
          href="http://www.tripadvisor.com/"
          className="ta-logo ta-member"
          dangerouslySetInnerHTML={{ __html: talogo }}
        ></a>
      </div>
      <div className="ta-member ta-reviews-cell">
        <h3>{tareviewtext}</h3>
        <div className="ta-total-reviews">
          {overallRatingImage && (
            <img
              src={overallRatingImage}
              alt="TripAdvisor Rating"
              className="ta-reviews-image"
            />
          )}
          {webUrl && numReviews && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={webUrl}
              className="ta-num-reviews"
            >
              {numReviews} Review{numReviews !== "1" ? "s" : ""}
            </a>
          )}
        </div>
        {/* Only show review section if a current review exists */}
        {currentReview && (
          <div className="ta-review ta-member">
            <div className="ta-title-rating">
              <h4>
                "{currentReview.title}"
                {currentReview.rating_image_url && (
                  <img
                    src={currentReview.rating_image_url}
                    alt="TripAdvisor Review Rating"
                    className="ta-reviews-image"
                  />
                )}
              </h4>
            </div>
            <div className="ta-rating-text">
              <p>{currentReview.text}</p>
            </div>
            <div className="ta-read-full ta-outbound">
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={currentReview.url}
              >
                {readfullreviewtext}
              </a>
            </div>
          </div>
        )}
        {/* Show message if no reviews */}
        {!currentReview && numReviews === "0" && (
          <p>No reviews available yet.</p>
        )}
      </div>
      <div className="ta-member ta-outbound">
        {webUrl && (
          <a target="_blank" rel="noopener noreferrer" href={webUrl}>
            {outboundtext}
          </a>
        )}
      </div>
      <div className="ta-member ta-write-review ta-outbound ta-gray">
        {writeUrl && (
          <a target="_blank" rel="noopener noreferrer" href={writeUrl}>
            {writereviewtext}
          </a>
        )}
      </div>
      <div className="ta-member ta-terms ta-gray">
        <a
          target="_blank"
          rel="noopener noreferrer"
          href="http://www.tripadvisor.com/pages/terms.html"
        >
          © TripAdvisor {new Date().getFullYear()}
        </a>
      </div>
    </div>
  );
};

TripAdvisor.propTypes = {
  location: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  apikey: PropTypes.string.isRequired,
  talogo: PropTypes.string, // HTML string, handle with care
  tareviewtext: PropTypes.string,
  readfullreviewtext: PropTypes.string,
  outboundtext: PropTypes.string,
  writereviewtext: PropTypes.string,
};

// Add default props for texts if they might be missing
TripAdvisor.defaultProps = {
  talogo: "",
  tareviewtext: "Reviews",
  readfullreviewtext: "Read full review",
  outboundtext: "View on TripAdvisor",
  writereviewtext: "Write a review",
};

export default TripAdvisor;
